import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Info, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo, Eyebrow, TripleSlash } from "@/components/brand/Logo";
import { api, formatApiError } from "@/lib/api";
import { ROLES, IMAGES } from "@/lib/constants";

const OTHER = "__other__";
const EMPTY = { name: "", email: "", phone: "", role_requested: "", community_id: "", community_name: "", message: "" };

export default function RequestAccess() {
  const [form, setForm] = useState(EMPTY);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/communities").then((r) => setCommunities(r.data));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.role_requested) return setError({ text: "Please choose the role you're requesting." });
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone || null, role_requested: form.role_requested,
        message: form.message || null,
        community_id: form.community_id && form.community_id !== OTHER ? form.community_id : null,
        community_name: form.community_id === OTHER ? form.community_name : null,
      };
      await api.post("/access-requests", payload);
      setDone(true);
    } catch (err) {
      setError({ text: formatApiError(err), friendly: err.response?.status === 409 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lasso-paper">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" data-testid="request-brand-link"><Logo size="sm" /></Link>
        <Link to="/login" className="text-sm font-semibold underline-offset-4 hover:underline" data-testid="request-sign-in-link">Sign in</Link>
      </header>
      <div className="mx-auto grid max-w-6xl gap-12 px-6 pb-24 pt-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="fade-up">
          <Eyebrow>Request access</Eyebrow>
          <h1 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">Join your team on Lasso Hub.</h1>
          <p className="mt-5 max-w-md text-muted-foreground">Tell us who you are and where you work. An administrator reviews every request, assigns your role and community, and sends you sign-in details.</p>
          <div className="relative mt-10 hidden overflow-hidden rounded-2xl lg:block">
            <img src={IMAGES.team} alt="Lasso team reviewing an SOP" className="aspect-[4/3] w-full object-cover" />
            <div className="absolute bottom-4 left-4 rounded-xl bg-white/90 px-4 py-3 backdrop-blur-md">
              <TripleSlash />
              <p className="mt-2 text-sm font-semibold">Reviewed by your admin team, usually within 1 business day.</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-lasso-line bg-white p-6 shadow-[0_10px_40px_rgba(11,11,12,0.05)] sm:p-10">
          {done ? (
            <div className="py-12 text-center" data-testid="request-access-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-lasso-deep" />
              <h2 className="mt-6 font-display text-2xl font-bold">Request received</h2>
              <p className="mx-auto mt-3 max-w-sm text-muted-foreground">Thanks, {form.name.split(" ")[0]}. An administrator will review your request and reach out at {form.email}.</p>
              <Link to="/" className="mt-8 inline-block rounded-full bg-lasso-ink px-6 py-3 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="request-access-back-home">Back to home</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="request-access-form">
              {error && (
                <div
                  data-testid="request-access-error"
                  className={`flex gap-3 rounded-lg border p-3 text-sm ${error.friendly ? "border-lasso-sky bg-lasso-tint text-lasso-navy" : "border-rose-200 bg-rose-50 text-rose-800"}`}
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {error.text}{" "}
                    {error.friendly && error.text.includes("sign in") && (
                      <Link to="/login" className="font-semibold underline" data-testid="request-access-error-sign-in">Go to sign in</Link>
                    )}
                  </span>
                </div>
              )}
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="ra-name">Full name</Label>
                  <Input id="ra-name" required value={form.name} onChange={set("name")} className="h-11" data-testid="request-access-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ra-email">Work email</Label>
                  <Input id="ra-email" type="email" required value={form.email} onChange={set("email")} className="h-11" data-testid="request-access-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ra-phone">Phone <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="ra-phone" value={form.phone} onChange={set("phone")} className="h-11" data-testid="request-access-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Role requested</Label>
                  <Select value={form.role_requested || undefined} onValueChange={set("role_requested")}>
                    <SelectTrigger className="h-11" data-testid="request-access-role-select"><SelectValue placeholder="Choose a role" /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`request-access-role-${r.value}`}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Community</Label>
                <Select value={form.community_id || undefined} onValueChange={set("community_id")}>
                  <SelectTrigger className="h-11" data-testid="request-access-community-select"><SelectValue placeholder="Where do you work?" /></SelectTrigger>
                  <SelectContent>
                    {communities.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`request-access-community-${c.id}`}>{c.name} · {c.city}, {c.state}</SelectItem>
                    ))}
                    <SelectItem value={OTHER} data-testid="request-access-community-other">Other / corporate office</SelectItem>
                  </SelectContent>
                </Select>
                {form.community_id === OTHER && (
                  <Input placeholder="Community or office name" value={form.community_name} onChange={set("community_name")} className="mt-2 h-11" data-testid="request-access-community-name-input" />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ra-msg">Anything else? <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea id="ra-msg" rows={3} value={form.message} onChange={set("message")} data-testid="request-access-message-input" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="request-access-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-lasso-navy disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit request
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
